import { useEffect, useRef } from 'react';
import { ChannelName } from '@/services/websocket/websocket.types';
import { subscribe } from '@/services/websocket/ws-client';

interface UseWebsocketOptions {
  // Fired when the socket returns after a failed attempt, never on a first connect.
  // Missed frames are not replayed, so consumers resync here.
  onReconnect?: () => void;
}

// One connection per channel, path-addressed. There is no subscribe command and no
// envelope: the payload IS the frame. Reconnect, backoff and close-code policy all live
// in ws-client, so this hook is only the React binding.
export const useWebsocket = <T>(
  channel: ChannelName,
  received: (data: T) => void,
  { onReconnect }: UseWebsocketOptions = {}
): void => {
  const receivedRef = useRef(received);
  const reconnectRef = useRef(onReconnect);

  useEffect(() => {
    receivedRef.current = received;
  }, [received]);

  useEffect(() => {
    reconnectRef.current = onReconnect;
  }, [onReconnect]);

  // `channel` is a plain string literal, so it is a stable dep and both callbacks are held
  // in refs. Call sites can keep passing fresh inline arrows every render.
  useEffect(() => {
    return subscribe(
      channel,
      (data) => {
        receivedRef.current(data as T);
      },
      () => {
        reconnectRef.current?.();
      }
    );
  }, [channel]);
};
